import { ScreenSpinner } from "@/components/ui/spinner";
import {
  acceptTrelloRedirect,
  finishTrelloConnect,
  holdTrelloToken,
} from "@/lib/auth/connect";
import { tokenManager } from "@/lib/auth/token-manager";
import { scrubLocationSecrets } from "@/lib/auth/url";
import { captureEvent, sanitizeErrorReason } from "@/lib/analytics";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/connect-trello")({
  component: RouteComponent,
});

const home = {
  to: "/",
  search: { change_password_token: undefined, reset_password_token: undefined },
} as const;

function RouteComponent() {
  const navigate = useNavigate();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    const token = acceptTrelloRedirect(
      window.location.hash,
      window.location.search,
    );
    scrubLocationSecrets(["nonce"]);

    const run = async () => {
      if (!token) {
        toast.error(
          "That Trello link did not start here. Please connect again.",
        );
        navigate(home);
        return;
      }

      captureEvent("onboarding_connect_started", {
        service: "trello",
        flow: "callback",
      });
      await tokenManager.ready;
      if (!tokenManager.isSignedIn()) {
        holdTrelloToken(token);
        toast.info("Sign in to finish connecting Trello.");
        navigate(home);
        return;
      }

      try {
        await finishTrelloConnect(token);
        captureEvent("onboarding_connect_succeeded", {
          service: "trello",
          flow: "callback",
        });
        toast.success("Trello connected");
      } catch (err) {
        captureEvent("onboarding_connect_failed", {
          service: "trello",
          flow: "callback",
          reason: sanitizeErrorReason(err),
        });
        toast.error("Could not connect Trello. Please try again.");
      }
      navigate(home);
    };

    void run();
  }, [navigate]);

  return (
    <div className="bg-background flex min-h-screen w-screen items-center justify-center">
      <ScreenSpinner />
    </div>
  );
}
